import { useEffect, useRef, useState } from "react";
import { Mic, Square, Play, Pause, Trash2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const MAX_SECONDS = 60;

function pickMimeType() {
  // Prefer webm/opus (Chrome/Firefox), fall back to mp4 (Safari)
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  if (typeof window === "undefined" || !window.MediaRecorder) return "";
  for (const m of candidates) {
    try {
      if (window.MediaRecorder.isTypeSupported(m)) return m;
    } catch (err) {
      // Some browsers throw on unknown codec strings — keep probing the rest.
      // eslint-disable-next-line no-console
      console.debug("MediaRecorder.isTypeSupported probe failed for", m, err);
    }
  }
  return "";
}

/**
 * VoiceRecorder — browser MediaRecorder wrapper.
 *
 * Props:
 *  - onChange({ blob, mime, duration }) — called when a recording is ready, or
 *      with null when cleared.
 *  - disabled — boolean, locks the controls (e.g. while parent is busy).
 *  - max — max recording length in seconds (default 60).
 */
export default function VoiceRecorder({ onChange, disabled = false, max = MAX_SECONDS }) {
  const [supported, setSupported] = useState(true);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const elapsedRef = useRef(0);
  const blobUrlRef = useRef(null);

  // Keep a ref mirror of blobUrl so the unmount cleanup always sees the latest URL
  // without re-binding the effect (avoids tearing down/setting up on every blobUrl change).
  useEffect(() => {
    blobUrlRef.current = blobUrl;
  }, [blobUrl]);

  // Probe support once on mount
  useEffect(() => {
    const has = typeof navigator !== "undefined"
      && navigator.mediaDevices
      && typeof navigator.mediaDevices.getUserMedia === "function"
      && typeof window !== "undefined"
      && typeof window.MediaRecorder !== "undefined";
    setSupported(!!has);
  }, []);

  // Cleanup on unmount only — no deps needed because refs always read latest value
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  const stop = () => {
    try {
      mediaRecorderRef.current?.stop();
    } catch (err) {
      // MediaRecorder.stop() throws if recorder was never started or already stopped — safe to ignore.
      // eslint-disable-next-line no-console
      console.debug("MediaRecorder.stop() ignored:", err);
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setRecording(false);
  };

  const start = async () => {
    if (disabled || recording) return;
    if (!supported) {
      toast.error("Your browser doesn't support voice recording.");
      return;
    }
    // Clear any previous recording
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(null);
    setDuration(0);
    setElapsed(0);

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      toast.error("Microphone permission denied. Allow mic access to leave a voice note.");
      return;
    }
    streamRef.current = stream;
    const mime = pickMimeType();
    let mr;
    try {
      mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch (err) {
      toast.error("Couldn't start the recorder. Try a different browser.");
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    mediaRecorderRef.current = mr;
    chunksRef.current = [];

    mr.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    mr.onstop = () => {
      const actualMime = mr.mimeType || mime || "audio/webm";
      const blob = new Blob(chunksRef.current, { type: actualMime });
      const url = URL.createObjectURL(blob);
      const dur = elapsedRef.current; // capture latest elapsed
      setBlobUrl(url);
      setDuration(dur);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (onChange) onChange({ blob, mime: actualMime, duration: dur });
    };

    mr.start();
    setRecording(true);

    const startedAt = Date.now();
    timerRef.current = setInterval(() => {
      const secs = Math.floor((Date.now() - startedAt) / 1000);
      elapsedRef.current = secs;
      setElapsed(secs);
      if (secs >= max) {
        stop();
      }
    }, 250);
  };

  // Keep elapsed accessible inside MediaRecorder.onstop without stale closure
  const clear = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(null);
    setDuration(0);
    setElapsed(0);
    setPlaying(false);
    if (onChange) onChange(null);
  };

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play();
    } else {
      el.pause();
    }
  };

  if (!supported) {
    return (
      <div
        className="surface p-4 text-sm text-white/55 flex items-start gap-3"
        data-testid="voice-not-supported"
      >
        <AlertCircle size={16} style={{ color: "var(--gold)" }} className="mt-0.5 shrink-0" />
        <p>
          Voice notes require a modern browser with microphone access (Chrome,
          Firefox, Safari 14.5+). Try the text form below instead.
        </p>
      </div>
    );
  }

  const renderTime = (s) => {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, "0")}`;
  };

  return (
    <div
      className="surface p-5 lg:p-6 space-y-4"
      data-testid="voice-recorder"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {!recording && !blobUrl && (
            <button
              type="button"
              onClick={start}
              disabled={disabled}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full text-xs uppercase tracking-[0.22em] border border-[color:var(--gold)]/60 text-[color:var(--gold)] hover:bg-[color:var(--gold)]/10 disabled:opacity-40 transition"
              data-testid="voice-record-start"
            >
              <Mic size={14} />
              Record Voice Note
            </button>
          )}
          {recording && (
            <button
              type="button"
              onClick={stop}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full text-xs uppercase tracking-[0.22em] bg-red-500/20 border border-red-400/60 text-red-200 hover:bg-red-500/30 transition"
              data-testid="voice-record-stop"
            >
              <Square size={12} />
              Stop ({renderTime(elapsed)})
            </button>
          )}
          {!recording && blobUrl && (
            <>
              <button
                type="button"
                onClick={togglePlay}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full text-xs uppercase tracking-[0.22em] border border-white/15 text-white/85 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] transition"
                data-testid="voice-toggle-playback"
              >
                {playing ? <Pause size={14} /> : <Play size={14} />}
                {playing ? "Pause" : "Preview"}
              </button>
              <button
                type="button"
                onClick={clear}
                className="inline-flex items-center gap-2 h-11 px-4 rounded-full text-xs uppercase tracking-[0.22em] text-white/55 hover:text-red-300 transition"
                data-testid="voice-clear"
              >
                <Trash2 size={13} />
                Re-record
              </button>
            </>
          )}
        </div>
        <span className="text-[10px] uppercase tracking-[0.22em] text-white/40 shrink-0">
          {recording
            ? `Recording · ${renderTime(elapsed)} / ${renderTime(max)}`
            : blobUrl
              ? `Captured · ${renderTime(duration)}`
              : `Max ${max}s`}
        </span>
      </div>

      {recording && (
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-red-300/85" data-testid="voice-recording-indicator">
          <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
          Recording in progress…
        </div>
      )}

      {blobUrl && !recording && (
        <audio
          ref={audioRef}
          src={blobUrl}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          controls
          className="w-full mt-1"
          data-testid="voice-preview-audio"
        />
      )}
    </div>
  );
}

// Helper export so callers can validate before submit
export function voiceRecordingIsReady(rec) {
  return !!(rec && rec.blob && rec.blob.size > 0);
}
